import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";
import {
  ArrowRight, ArrowUpRight, FileText, ScanSearch, Send, Inbox,
  Landmark, Archive, Smartphone, ShieldCheck, Zap, Check, Play,
} from "lucide-react";
import api, { API_ORIGIN } from "@/lib/api";
import ComparisonSection from "@/components/ComparisonSection";

const HERO_IMG = "https://images.unsplash.com/photo-1678366633407-7f49da199a42?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjBlbGVnYW50JTIwM2QlMjBzaGFwZXN8ZW58MHx8fHwxNzg3OTkwNzk0fDA&ixlib=rb-4.1.0&q=85";
const MANIFESTO_IMG = "https://images.unsplash.com/photo-1758520145132-b0ecdb967295?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMHRlYW0lMjBwcm9mZXNzaW9uYWwlMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODc5OTA3OTN8MA&ixlib=rb-4.1.0&q=85";

const FALLBACK_PLANS = [
  { id: "starter", name: "Starter", price: 9, period: "mois", quota: "30 conversions", features: ["Import PDF illimité", "Extraction IA", "30 conversions Factur-X / mois", "Archivage 10 ans"] },
  { id: "pro", name: "Pro", price: 19, period: "mois", quota: "Illimité", features: ["Conversions illimitées", "Formats Factur-X / UBL / CII", "Envoi PDP / PPF", "Support prioritaire"] },
  { id: "enterprise", name: "Entreprise", price: 49, period: "mois", quota: "Multi-utilisateurs", features: ["Tout Pro inclus", "Multi-utilisateurs", "IA avancée", "API & intégrations"] },
];

const MARQUEE = ["FACTUR-X", "EN 16931", "PDP", "PPF", "CHORUS PRO", "OCTROI DE MER", "UBL 2.1", "CII", "GUADELOUPE", "MARTINIQUE", "GUYANE", "LA RÉUNION", "MAYOTTE"];

const RevealLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const SectionReveal = ({ children, className = "", delay = 0 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 48 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.div>
);

const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
    <span className="h-8 w-8 rounded-lg bg-gradient-to-br from-[#2563EB] to-[#00D4FF] flex items-center justify-center font-display text-[13px] font-800 text-[#050505] font-extrabold transition-transform duration-300 group-hover:rotate-6">
      FX
    </span>
    <span className="font-display text-sm tracking-tight text-white">
      Factur<span className="text-[#00D4FF]">Xpress</span>
    </span>
  </Link>
);

export default function Landing() {
  const [plans, setPlans] = useState(FALLBACK_PLANS);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.08, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  useEffect(() => {
    api.get("/plans").then(({ data }) => {
      if (Array.isArray(data?.plans) && data.plans.length) setPlans(data.plans);
    }).catch(() => {});
  }, []);

  return (
    <div className="bg-[#050505] text-white min-h-screen" data-testid="landing-page">
      <div className="noise-overlay" />

      {/* NAV */}
      <header className="fixed top-0 inset-x-0 z-50 glass-panel border-x-0 border-t-0" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Logo />
          <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-400">
            <a href="#fonctionnalites" data-testid="nav-features" className="hover:text-white transition-colors duration-200">Fonctionnalités</a>
            <a href="#manifeste" data-testid="nav-manifesto" className="hover:text-white transition-colors duration-200">Manifeste</a>
            <a href="#tarifs" data-testid="nav-pricing" className="hover:text-white transition-colors duration-200">Tarifs</a>
            <a href="#comparatif" data-testid="nav-comparison" className="hover:text-white transition-colors duration-200">Comparatif</a>
            <Link to="/guide" data-testid="nav-guide" className="hover:text-white transition-colors duration-200">Guide</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/connexion" data-testid="nav-login" className="text-sm text-zinc-300 hover:text-white transition-colors duration-200 px-3 py-2">
              Connexion
            </Link>
            <Link
              to="/connexion"
              data-testid="nav-cta"
              className="rounded-full bg-white text-[#050505] text-sm font-semibold px-5 py-2 hover:bg-[#00D4FF] hover:-translate-y-0.5 transition-[transform,background-color] duration-200"
            >
              Lancer l'app
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] flex items-end overflow-hidden" data-testid="hero-section">
        <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-[#050505]/30" />
        </motion.div>
        <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_70%_20%,rgba(37,99,235,0.22),transparent_70%)]" />

        <motion.div style={{ opacity: heroOpacity }} className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-24 pt-40 w-full">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 backdrop-blur-md px-4 py-1.5 mb-8"
            data-testid="hero-badge"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#34C759] animate-pulse" />
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-300">Factur-X · PDP · Octroi de mer</span>
          </motion.div>

          <h1 className="font-display font-light tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[1.02]" data-testid="hero-title">
            <RevealLine delay={0.25}>La facturation</RevealLine>
            <RevealLine delay={0.38}>
              <span className="font-extrabold">électronique,</span>
            </RevealLine>
            <RevealLine delay={0.51}>
              enfin <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] to-[#00D4FF] font-extrabold">limpide.</span>
            </RevealLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-8 max-w-xl text-base md:text-lg text-zinc-400 leading-relaxed"
            data-testid="hero-subtitle"
          >
            Déposez un PDF, l'IA extrait chaque ligne. Générez du Factur-X conforme EN 16931,
            envoyez via votre PDP, calculez l'octroi de mer DOM-TOM. Vos données restent
            synchronisées avec l'application mobile.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/connexion?mode=inscription"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 rounded-full bg-[#2563EB] px-7 py-3.5 text-sm font-semibold hover:bg-[#00D4FF] hover:text-[#050505] hover:-translate-y-0.5 transition-[transform,background-color,color] duration-300"
            >
              Essayer gratuitement
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <a
              href="#manifeste"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-sm text-zinc-300 hover:border-[#00D4FF] hover:text-white hover:-translate-y-0.5 transition-[transform,border-color,color] duration-300"
            >
              Lire le manifeste
            </a>
            <p className="w-full text-xs text-zinc-500 font-mono tracking-wide" data-testid="hero-trial-note">
              3 factures offertes à l'inscription — sans carte bancaire
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.15 }}
            className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden max-w-3xl"
            data-testid="hero-stats"
          >
            {[["10 ans", "d'archivage légal"], ["3 formats", "Factur-X · UBL · CII"], ["5+", "PDP connectées"], ["100%", "DOM-TOM natif"]].map(([v, l]) => (
              <div key={l} className="bg-[#050505]/90 backdrop-blur px-5 py-4">
                <p className="font-display text-lg text-white">{v}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{l}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-white/10 bg-[#0A0E17] py-5 overflow-hidden" data-testid="marquee-section">
        <div className="marquee-track">
          {[0, 1].map((copy) => (
            <div key={copy} className="flex shrink-0 items-center">
              {MARQUEE.map((item) => (
                <span key={`${copy}-${item}`} className="flex items-center">
                  <span className="font-display text-sm tracking-[0.2em] text-zinc-500 px-8 whitespace-nowrap">{item}</span>
                  <span className="h-1 w-1 rounded-full bg-[#00D4FF]/60" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* DEMO — preuve produit en video, placement strategique post-hero */}
      <section id="demo" className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="demo-section">
        <SectionReveal className="text-center">
          <p className="overline-label mb-4">Démonstration</p>
          <h2 className="font-display font-light tracking-tighter text-4xl md:text-5xl leading-tight">
            Pas de promesse. <span className="text-[#00D4FF]">Une preuve.</span>
          </h2>
          <p className="mt-5 text-sm md:text-base text-zinc-400 max-w-xl mx-auto">
            Regardez FacturXpress travailler : 27 secondes, sans montage, dans la vraie application.
          </p>
        </SectionReveal>

        <div className="mt-14 grid md:grid-cols-2 gap-6 md:gap-8">
          {[
            {
              src: "/demos/demo-import.mp4",
              poster: "/demos/poster-import.jpg",
              tag: "Import PDF + IA",
              title: "Déposez un PDF. L'IA extrait tout.",
              desc: "SIRET, TVA, lignes de facturation — zéro ressaisie.",
            },
            {
              src: "/demos/demo-creation.mp4",
              poster: "/demos/poster-creation.jpg",
              tag: "Création de zéro",
              title: "Ou créez une facture en 2 minutes.",
              desc: "Modèles par métier, totaux automatiques, octroi de mer inclus.",
            },
          ].map((v) => (
            <SectionReveal key={v.src} className="group">
              <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#0A0E17] transition-colors duration-300 group-hover:border-[#00D4FF]/40">
                <div className="absolute -inset-10 bg-[#2563EB]/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                <video
                  className="relative w-full aspect-video object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                  preload="metadata"
                  poster={v.poster}
                  data-testid={`demo-video-${v.tag.includes("Import") ? "import" : "creation"}`}
                >
                  <source src={v.src} type="video/mp4" />
                </video>
              </div>
              <div className="mt-5 flex items-start gap-3">
                <span className="shrink-0 mt-0.5 inline-flex items-center gap-1.5 rounded-full border border-[#2563EB]/50 bg-[#2563EB]/10 text-[#7FA6FF] px-3 py-1 text-[11px] font-semibold tracking-wide">
                  <Play className="h-3 w-3" /> {v.tag}
                </span>
                <div>
                  <p className="font-display text-base md:text-lg text-white">{v.title}</p>
                  <p className="text-sm text-zinc-500 mt-1">{v.desc}</p>
                </div>
              </div>
            </SectionReveal>
          ))}
        </div>

        <SectionReveal className="mt-12 text-center" delay={0.1}>
          <Link
            to="/connexion?mode=inscription"
            data-testid="demo-cta"
            className="group inline-flex items-center gap-2 rounded-full bg-[#2563EB] px-7 py-3.5 text-sm font-semibold hover:bg-[#00D4FF] hover:text-[#050505] hover:-translate-y-0.5 transition-[transform,background-color,color] duration-300"
          >
            Essayer gratuitement — 3 factures offertes
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </SectionReveal>
      </section>

      {/* MANIFESTO */}
      <section id="manifeste" className="max-w-7xl mx-auto px-6 md:px-10 py-28 md:py-40" data-testid="manifesto-section">
        <SectionReveal>
          <p className="overline-label mb-4">Manifeste</p>
          <h2 className="font-display font-light tracking-tighter text-4xl md:text-5xl max-w-2xl leading-tight">
            Trois convictions, <span className="text-[#00D4FF]">zéro compromis.</span>
          </h2>
        </SectionReveal>

        <div className="mt-20 space-y-24">
          {[
            {
              n: "01",
              title: "Le PDF n'est qu'un point de départ.",
              text: "Vos factures arrivent en PDF. Notre IA lit chaque ligne, chaque taux, chaque SIRET — et structure tout en données exploitables. Vous ne ressaisissez plus rien.",
            },
            {
              n: "02",
              title: "La conformité n'est pas une option.",
              text: "Factur-X, UBL, CII. EN 16931. Cycle de vie PDP, statuts en temps réel, accusés. La réforme 2026-2027 arrive : vous êtes déjà prêt.",
            },
            {
              n: "03",
              title: "Les DOM-TOM ne sont pas un cas particulier.",
              text: "Octroi de mer, octroi de mer régional, exonérations TVA : calculés nativement par ligne de facture. Guadeloupe, Martinique, Guyane, La Réunion, Mayotte — même exigence, même outil.",
            },
          ].map((c, i) => (
            <SectionReveal key={c.n} delay={0.05}>
              <div className={`grid md:grid-cols-12 gap-8 items-start ${i % 2 ? "" : ""}`}>
                <span className="md:col-span-3 font-display text-7xl md:text-8xl font-light text-transparent [-webkit-text-stroke:1px_rgba(255,255,255,0.25)] select-none">
                  {c.n}
                </span>
                <div className="md:col-span-9 border-l border-white/10 pl-8">
                  <h3 className="font-display text-2xl md:text-3xl tracking-tight">{c.title}</h3>
                  <p className="mt-4 text-zinc-400 leading-relaxed max-w-2xl">{c.text}</p>
                </div>
              </div>
            </SectionReveal>
          ))}
        </div>

        <SectionReveal className="mt-28">
          <div className="relative rounded-3xl overflow-hidden border border-white/10">
            <img src={MANIFESTO_IMG} alt="Équipe FacturXpress" className="w-full h-72 md:h-96 object-cover" />
            <div className="absolute inset-0 bg-black/50" />
            <div className="absolute bottom-0 left-0 p-8 md:p-12">
              <p className="font-display text-xl md:text-2xl max-w-lg leading-snug">
                « La même rigueur qu'une banque. La même vitesse qu'une startup. »
              </p>
              <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400">L'équipe FacturXpress</p>
            </div>
          </div>
        </SectionReveal>
      </section>

      {/* FEATURES — Tetris grid */}
      <section id="fonctionnalites" className="max-w-7xl mx-auto px-6 md:px-10 pb-28 md:pb-40" data-testid="features-section">
        <SectionReveal>
          <p className="overline-label mb-4">Fonctionnalités</p>
          <h2 className="font-display font-light tracking-tighter text-4xl md:text-5xl max-w-3xl leading-tight">
            Tout le cycle de vie, <span className="font-extrabold">une seule plateforme.</span>
          </h2>
        </SectionReveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-6">
          <SectionReveal className="md:col-span-7" delay={0}>
            <FeatureCard
              icon={<ScanSearch className="h-6 w-6" />}
              title="Import PDF + extraction IA"
              text="Glissez une facture PDF : l'IA détecte vendeur, acheteur, lignes, TVA et octroi de mer. Correction assistée en un clic."
              tag="IA"
              big
            />
          </SectionReveal>
          <SectionReveal className="md:col-span-5" delay={0.08}>
            <FeatureCard
              icon={<FileText className="h-6 w-6" />}
              title="Génération Factur-X"
              text="Conversion conforme EN 16931 en Factur-X, UBL ou CII. XML embarqué, prêt pour le dépôt."
              tag="EN 16931"
            />
          </SectionReveal>
          <SectionReveal className="md:col-span-5" delay={0}>
            <FeatureCard
              icon={<Send className="h-6 w-6" />}
              title="Envoi & réception PDP"
              text="Transmission via votre Plateforme de Dématérialisation Partenaire ou le PPF. Statuts synchronisés, décisions accept / refus."
              tag="PDP · PPF"
            />
          </SectionReveal>
          <SectionReveal className="md:col-span-7" delay={0.08}>
            <FeatureCard
              icon={<Landmark className="h-6 w-6" />}
              title="Octroi de mer DOM-TOM"
              text="Calcul par ligne, taux normaux et spécifiques, totaux séparés HT / TVA / octroi. Pensé pour la Guadeloupe, la Martinique, la Guyane, La Réunion et Mayotte."
              tag="DOM-TOM"
              big
            />
          </SectionReveal>
          <SectionReveal className="md:col-span-4" delay={0}>
            <FeatureCard
              icon={<Inbox className="h-6 w-6" />}
              title="Réception fournisseurs"
              text="Factures entrantes centralisées, vérifiées, archivées."
            />
          </SectionReveal>
          <SectionReveal className="md:col-span-4" delay={0.06}>
            <FeatureCard
              icon={<Archive className="h-6 w-6" />}
              title="Archives 10 ans"
              text="Conservation probante, export complet à tout moment."
            />
          </SectionReveal>
          <SectionReveal className="md:col-span-4" delay={0.12}>
            <FeatureCard
              icon={<Smartphone className="h-6 w-6" />}
              title="Web · iOS · Android"
              text="PWA installable. Mêmes comptes, mêmes données que l'app mobile."
            />
          </SectionReveal>
        </div>
      </section>

      {/* COMPARATIF */}
      <ComparisonSection />

      {/* PRICING */}
      <section id="tarifs" className="border-t border-white/10 bg-[#0A0E17]" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-28 md:py-36">
          <SectionReveal>
            <p className="overline-label mb-4">Tarifs</p>
            <h2 className="font-display font-light tracking-tighter text-4xl md:text-5xl leading-tight">
              Un prix par ambition.
            </h2>
            <p className="mt-5 text-sm md:text-base text-zinc-400 max-w-xl" data-testid="pricing-trial-note">
              Commencez gratuitement : <span className="text-[#00D4FF] font-semibold">3 factures offertes</span> à l'inscription, sans carte bancaire. Passez à une formule quand vous êtes convaincu.
            </p>
          </SectionReveal>

          <div className="mt-16 grid md:grid-cols-3 gap-6" data-testid="pricing-grid">
            {plans.map((p, i) => {
              const popular = p.id === "pro";
              return (
                <SectionReveal key={p.id} delay={i * 0.08}>
                  <div
                    data-testid={`plan-card-${p.id}`}
                    className={`relative rounded-2xl p-8 h-full flex flex-col bg-[#050505] border ${popular ? "beam-border border-transparent" : "border-white/10"} hover:-translate-y-1 transition-transform duration-300`}
                  >
                    {popular && (
                      <span className="absolute -top-3 left-8 rounded-full bg-[#00D4FF] text-[#050505] font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1">
                        Recommandé
                      </span>
                    )}
                    <h3 className="font-display text-lg">{p.name}</h3>
                    <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">{p.quota}</p>
                    <p className="mt-6">
                      <span className="font-display text-5xl font-light">{p.price}€</span>
                      <span className="text-zinc-500 text-sm"> / {p.period}</span>
                    </p>
                    <ul className="mt-8 space-y-3 flex-1">
                      {(p.features || []).map((f) => (
                        <li key={f} className="flex items-start gap-3 text-sm text-zinc-300">
                          <Check className="h-4 w-4 mt-0.5 text-[#34C759] shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/connexion?mode=inscription"
                      data-testid={`plan-cta-${p.id}`}
                      className={`mt-8 inline-flex justify-center items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-[transform,background-color,color] duration-200 hover:-translate-y-0.5 ${
                        popular
                          ? "bg-[#2563EB] text-white hover:bg-[#00D4FF] hover:text-[#050505]"
                          : "border border-white/20 text-white hover:border-[#00D4FF]"
                      }`}
                    >
                      Commencer
                      <ArrowUpRight className="h-4 w-4" />
                    </Link>
                  </div>
                </SectionReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden" data-testid="final-cta">
        <div className="absolute inset-0 bg-[radial-gradient(50%_60%_at_50%_100%,rgba(37,99,235,0.25),transparent_70%)]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-28 md:py-36 text-left">
          <SectionReveal>
            <p className="overline-label mb-6">Prêt pour 2026</p>
            <h2 className="font-display font-light tracking-tighter text-4xl md:text-6xl max-w-3xl leading-[1.05]">
              Vos factures méritent <span className="font-extrabold text-[#00D4FF]">mieux qu'un tableur.</span>
            </h2>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/connexion?mode=inscription"
                data-testid="final-cta-button"
                className="group inline-flex items-center gap-2 rounded-full bg-white text-[#050505] px-8 py-4 text-sm font-bold hover:bg-[#00D4FF] hover:-translate-y-0.5 transition-[transform,background-color] duration-300"
              >
                <Zap className="h-4 w-4" />
                Essayer FacturXpress gratuitement
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
            <p className="mt-6 flex items-center gap-2 text-sm text-zinc-500">
              <ShieldCheck className="h-4 w-4 text-[#34C759]" />
              3 factures offertes, sans carte bancaire. Vos identifiants de l'app mobile fonctionnent ici.
            </p>
          </SectionReveal>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/10" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <Logo />
          <div className="flex flex-wrap gap-6 text-sm text-zinc-500">
            <a href={`${API_ORIGIN}/api/legal/privacy`} target="_blank" rel="noreferrer" data-testid="footer-privacy" className="hover:text-white transition-colors duration-200">Confidentialité</a>
            <a href={`${API_ORIGIN}/api/legal/cgu`} target="_blank" rel="noreferrer" data-testid="footer-cgu" className="hover:text-white transition-colors duration-200">CGU</a>
            <Link to="/guide" data-testid="footer-guide" className="hover:text-white transition-colors duration-200">Guide</Link>
            <a href="mailto:contact@factur-xpress.fr" data-testid="footer-contact" className="hover:text-[#00D4FF] transition-colors duration-200">contact@factur-xpress.fr</a>
            <Link to="/connexion" data-testid="footer-login" className="hover:text-white transition-colors duration-200">Connexion</Link>
          </div>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-600">
            © 2026 FacturXpress — Web · iOS · Android
          </p>
        </div>
      </footer>
    </div>
  );
}

const FeatureCard = ({ icon, title, text, tag, big }) => (
  <div className="group relative h-full rounded-2xl border border-white/10 bg-[#0A0E17] p-8 md:p-10 overflow-hidden hover:border-[#00D4FF]/40 transition-colors duration-300">
    <div className="absolute -top-20 -right-20 h-48 w-48 rounded-full bg-[#2563EB]/10 blur-3xl group-hover:bg-[#00D4FF]/15 transition-colors duration-500" />
    <div className="relative">
      <div className="flex items-center justify-between">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#2563EB]/15 text-[#00D4FF] border border-[#2563EB]/30 group-hover:scale-110 transition-transform duration-300">
          {icon}
        </span>
        {tag && (
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 border border-white/10 rounded-full px-3 py-1">{tag}</span>
        )}
      </div>
      <h3 className={`mt-6 font-display tracking-tight ${big ? "text-2xl" : "text-xl"}`}>{title}</h3>
      <p className="mt-3 text-sm text-zinc-400 leading-relaxed max-w-md">{text}</p>
    </div>
  </div>
);
